import { ReactNode, useContext, useState, useRef, useEffect } from "react";

import { SysMessageLevel } from "../sys_messages/type";
import { SysMessagesContext } from "../sys_messages/context";
import { usePerspective } from "../app/provider";
import { VersionedDataContext } from "./context";
import { VersionedDataContextType } from "./type";
import {
  Run,
  useGetRunsSubscription,
} from "../../services/perspective_api/__generated__/perspective_server-types";

const { Provider } = VersionedDataContext;

export const VersionedDataProvider = (props: {
  children: ReactNode;
}): JSX.Element => {
  const { perspectiveApiService } = usePerspective();
  const { addSystemMessage } = useContext(SysMessagesContext);

  const [loading, setLoading] = useState<boolean>(true);

  const [runsOrdered, setRunsOrdered] = useState<Run[]>([]);
  const [lastSuccessfulRun, setLastSuccessfulRun] = useState<Run | null>(null);
  const [atomicPivotRuns, setAtomicPivotRuns] = useState<Run[]>([]);
  const [googleDriveSyncRuns, setGoogleDriveSyncRuns] = useState<Run[]>([]);
  const [engineInProgress, setEngineInProgress] = useState<boolean>(false);
  const [runInProgress, setRunInProgress] = useState<Run | null>(null);
  const [justTriggeredRun, setJustTriggeredRun] = useState<Run | null>(null);
  const [isSubscriptionSkipped, setIsSubscriptionSkipped] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  useGetRunsSubscription({
    client: perspectiveApiService.getClient(),
    onData: (data) => {
      setJustTriggeredRun(null);
      const runs = (data.data?.data?.getRuns.runs || []).sort((a, b) => {
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        if (aDate < bDate) {
          return 1;
        }
        return -1;
      });
      setRunsOrdered(runs);
      setLastSuccessfulRun(
        runs.find(
          (run) =>
            run.status === "RUN_COMPLETED" &&
            run.runSettings.workflowId === "workflow_atomic_pivots"
        ) || null
      );
      setAtomicPivotRuns(
        runs.filter(
          (run) => run.runSettings.workflowId === "workflow_atomic_pivots"
        )
      );
      setGoogleDriveSyncRuns(
        runs.filter((run) => run.runSettings.workflowId === "google_drive_sync")
      );
      setEngineInProgress(data.data?.data?.getRuns.engineInProgress || false);
      if (data.data?.data?.getRuns.engineInProgress && runs.length > 0) {
        const runInProgress = runs.find(
          (run) => run.status === "RUN_IN_PROGRESS"
        );
        setRunInProgress(runInProgress || null);
      } else {
        setRunInProgress(null);
      }
      // The following should have a condition that google_drive_sync is
      // supposed to be running before this happens
      // if (
      //   data.data?.data?.getRuns.engineInProgress === false &&
      //   runs[0].runSettings.workflowId === "google_drive_sync" &&
      //   runs[0].status === "RUN_COMPLETED" &&
      //   loading === false
      // ) {
      //   addSystemMessage({
      //     level: SysMessageLevel.WARNING,
      //     message: "No changes in the data since last sync, skipping the run.",
      //   });
      // }
      setLoading(false);
    },
    onError: async (error) => {
      setJustTriggeredRun(null);
      if (runsOrdered.length > 0) {
        const runInProgress = runsOrdered.find(
          (run) => run.status === "RUN_IN_PROGRESS"
        );
        setRunInProgress(runInProgress || null);
      } else {
        setRunInProgress(null);
      }

      // Temporarily skip the subscription
      setIsSubscriptionSkipped(true);
      // We don't check for expired tokens here, which requires
      // recreating the entire websocket connection. It's handled in the
      // app context provider: if the token is expired, then a new one is
      // fetched from cognito and the app provider instructs the wsLink to
      // be recreated for the graphql client.

      // Set up retry after 3 seconds
      retryTimeoutRef.current = setTimeout(() => {
        // Re-enable the subscription after timeout
        setIsSubscriptionSkipped(false);
      }, 3000);

      if (error?.message !== undefined) {
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message: error.message!,
        });
      }
    },
    skip: isSubscriptionSkipped,
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const [mainRun, setMainRun] = useState<Run | null>(null);
  const [comparedRun, setComparedRun] = useState<Run | null>(null);
  const [editedRun, setEditedRun] = useState<Run | null>(null);

  const value: VersionedDataContextType = {
    loading: loading,
    lastSuccessfulRun,
    atomicPivotRuns,
    googleDriveSyncRuns,
    engineInProgress,
    runInProgress,
    mainRun,
    setMainRun,
    comparedRun,
    setComparedRun,
    runsOrdered,
    justTriggeredRun,
    setJustTriggeredRun,
    editedRun,
    setEditedRun,
  };

  return <Provider value={value}>{props.children}</Provider>;
};
